/**
 * CmraQuestions — BM-H593 (23 Sep 2026).
 *
 * The one set of risk assessment questions. Extracted from
 * CompleteRiskAssessmentPanel.jsx (BM-H543) so FORF Section 2 asks exactly
 * what the matter-page CMRA asked, which the product owner ruled is the
 * spec:
 *   - the 8 Low / Medium / High factors (RiskAssessmentPanel, live band)
 *   - the LSAG 5.10.1 five-category checklist, each "Addressed" or "N/A"
 *     with a reason of at least 20 characters
 *   - a risk narrative of at least 80 characters, always
 *   - mitigating actions, required when the band is Medium or High
 *
 * Controlled component: the parent owns the state (see initialCmraState)
 * and decides what to do on submit. Validation mirrors
 * backend/hub/risk_assessment.py:_validate_aml_initial_cmra, which is the
 * server authority for both /api/risk/cmra/initial and
 * /api/forf/{ref}/section-2.
 */
import { useCallback } from "react";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { ClipboardCheck } from "lucide-react";
import RiskAssessmentPanel from "./RiskAssessmentPanel";

// Mirrors backend/hub/risk_assessment.py CMRA_LSAG_CATEGORIES. Kept as a
// local literal (not fetched) so it never silently drifts without a test
// noticing.
export const LSAG_CATEGORIES = [
  { id: "client", label: "Client risk" },
  { id: "geography", label: "Geography risk" },
  { id: "service", label: "Service / product risk" },
  { id: "transaction", label: "Transaction risk" },
  { id: "delivery_channel", label: "Delivery channel risk" },
];

export const NARRATIVE_MIN_CHARS = 80;
export const NA_REASON_MIN_CHARS = 20;

export function initialCmraState() {
  return {
    riskFactors: {},
    calculatedBand: null,
    narrative: "",
    mitigations: "",
    // checklist[categoryId] = { addressed: bool, na_reason: string }
    checklist: Object.fromEntries(
      LSAG_CATEGORIES.map((c) => [c.id, { addressed: true, na_reason: "" }]),
    ),
  };
}

export function cmraValidity(state) {
  const s = state || initialCmraState();
  const narrativeOk = (s.narrative || "").trim().length >= NARRATIVE_MIN_CHARS;
  const mitigationsRequired = s.calculatedBand === "Medium" || s.calculatedBand === "High";
  const mitigationsOk = !mitigationsRequired || (s.mitigations || "").trim().length > 0;
  const checklistOk = LSAG_CATEGORIES.every((c) => {
    const entry = (s.checklist || {})[c.id] || { addressed: false, na_reason: "" };
    return entry.addressed || (entry.na_reason || "").trim().length >= NA_REASON_MIN_CHARS;
  });
  const allFactorsRated = Object.keys(s.riskFactors || {}).length > 0 && s.calculatedBand != null;
  return {
    narrativeOk,
    mitigationsRequired,
    mitigationsOk,
    checklistOk,
    allFactorsRated,
    ok: allFactorsRated && narrativeOk && mitigationsOk && checklistOk,
  };
}

// The first failing rule, as a sentence for a toast. Null when valid.
export function cmraFirstProblem(state) {
  const v = cmraValidity(state);
  if (!v.allFactorsRated) return "Rate all 8 risk factors before submitting.";
  if (!v.checklistOk) {
    return `Every LSAG category must be addressed or marked N/A with a reason (min ${NA_REASON_MIN_CHARS} chars).`;
  }
  if (!v.narrativeOk) return `Risk narrative must be at least ${NARRATIVE_MIN_CHARS} characters.`;
  if (!v.mitigationsOk) return `Mitigating actions are required for a ${state.calculatedBand} band.`;
  return null;
}

export function checklistPayload(checklist) {
  return LSAG_CATEGORIES.map((c) => {
    const entry = (checklist || {})[c.id] || { addressed: true, na_reason: "" };
    return {
      category_id: c.id,
      addressed: !!entry.addressed,
      na_reason: entry.addressed ? null : (entry.na_reason || "").trim(),
    };
  });
}

export default function CmraQuestions({
  matterRef,
  value,
  onChange,
  submitAttempted = false,
  currentUserEmail = null,
  submissionId = null,
}) {
  const state = value || initialCmraState();
  const v = cmraValidity(state);

  const handleRiskChange = useCallback(
    ({ factors, calculated_band }) => {
      onChange({ riskFactors: factors, calculatedBand: calculated_band });
    },
    [onChange],
  );

  const toggleAddressed = (categoryId, addressed) => {
    const prev = state.checklist[categoryId] || { addressed: true, na_reason: "" };
    onChange({
      checklist: {
        ...state.checklist,
        [categoryId]: { ...prev, addressed, na_reason: addressed ? "" : prev.na_reason },
      },
    });
  };

  const setNaReason = (categoryId, text) => {
    onChange({
      checklist: {
        ...state.checklist,
        [categoryId]: { ...state.checklist[categoryId], na_reason: text },
      },
    });
  };

  return (
    <div className="space-y-6" data-testid="cmra-questions">
      <RiskAssessmentPanel
        riskFactors={state.riskFactors}
        onRiskChange={handleRiskChange}
        calculatedBand={state.calculatedBand}
        matterRef={matterRef}
        submissionId={submissionId}
        currentUserEmail={currentUserEmail}
      />

      {/* LSAG 5.10.1 factor checklist. */}
      <div className="p-4 rounded-lg border border-navy-border bg-navy-card/50" data-testid="cmra-lsag-checklist">
        <h4 className="text-sm font-semibold text-txt flex items-center gap-2 mb-3">
          <ClipboardCheck className="w-4 h-4 text-teal" />
          LSAG Risk Category Checklist
        </h4>
        <div className="space-y-3">
          {LSAG_CATEGORIES.map((cat) => {
            const entry = state.checklist[cat.id] || { addressed: true, na_reason: "" };
            return (
              <div key={cat.id} className="border border-navy-border rounded-md p-3" data-testid={`lsag-category-${cat.id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-txt">{cat.label}</span>
                  <div className="flex gap-1.5">
                    <button
                      type="button"
                      onClick={() => toggleAddressed(cat.id, true)}
                      className={`px-2.5 py-1 rounded text-xs font-medium border ${entry.addressed ? "bg-teal-dim text-teal border-teal" : "bg-navy-hover text-slate-400 border-navy-border"}`}
                      data-testid={`lsag-${cat.id}-addressed`}
                    >
                      Addressed
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleAddressed(cat.id, false)}
                      className={`px-2.5 py-1 rounded text-xs font-medium border ${!entry.addressed ? "bg-status-amber-dim text-status-amber border-amber-300" : "bg-navy-hover text-slate-400 border-navy-border"}`}
                      data-testid={`lsag-${cat.id}-na`}
                    >
                      N/A
                    </button>
                  </div>
                </div>
                {!entry.addressed && (
                  <Textarea
                    value={entry.na_reason}
                    onChange={(e) => setNaReason(cat.id, e.target.value)}
                    placeholder={`Explain why '${cat.label}' is N/A (min ${NA_REASON_MIN_CHARS} chars)...`}
                    rows={2}
                    className="mt-2"
                    data-testid={`lsag-${cat.id}-na-reason`}
                  />
                )}
              </div>
            );
          })}
        </div>
        {submitAttempted && !v.checklistOk && (
          <p className="text-xs text-status-red mt-2" data-testid="cmra-checklist-error">
            Every LSAG category must be addressed or marked N/A with a reason (min {NA_REASON_MIN_CHARS} chars).
          </p>
        )}
      </div>

      {/* Narrative + mitigations. */}
      <div className="p-4 rounded-lg border border-navy-border bg-navy-card/50" data-testid="cmra-narrative-section">
        <div>
          <Label className="text-txt-muted">
            Risk Narrative <span className="text-status-red">*</span>
          </Label>
          <Textarea
            value={state.narrative}
            onChange={(e) => onChange({ narrative: e.target.value })}
            placeholder={`Explain the risk assessment for this matter (min ${NARRATIVE_MIN_CHARS} characters)...`}
            rows={4}
            className="mt-1"
            data-testid="cmra-initial-narrative"
          />
          <p className="text-xs text-slate-500 mt-1" data-testid="cmra-narrative-counter">
            {(state.narrative || "").trim().length}/{NARRATIVE_MIN_CHARS} characters minimum
          </p>
          {submitAttempted && !v.narrativeOk && (
            <p className="text-xs text-status-red mt-1" data-testid="cmra-narrative-error">
              Risk narrative must be at least {NARRATIVE_MIN_CHARS} characters.
            </p>
          )}
        </div>

        <div className="mt-4">
          <Label className="text-txt-muted">
            Mitigating Actions {v.mitigationsRequired && <span className="text-status-red">*</span>}
            {!v.mitigationsRequired && (
              <span className="ml-2 text-xs text-slate-500 font-normal">(optional at Low band)</span>
            )}
          </Label>
          <Textarea
            value={state.mitigations}
            onChange={(e) => onChange({ mitigations: e.target.value })}
            placeholder={
              v.mitigationsRequired
                ? `Required for ${state.calculatedBand} band — describe the mitigating steps taken.`
                : "Optional — describe mitigations if useful."
            }
            rows={3}
            className="mt-1"
            data-testid="cmra-initial-mitigations"
          />
          {submitAttempted && !v.mitigationsOk && (
            <p className="text-xs text-status-red mt-1" data-testid="cmra-mitigations-error">
              Mitigating actions required for a {state.calculatedBand} band.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
